import React from 'react';
import { Check, Copy, Link as LinkIcon, Mail, Phone, Plus, Users, X } from 'lucide-react';
import { Button } from '../ui/Button';

interface ContactsPanelProps {
  activeContact: any;
  contacts: any[];
  companyId?: string | null;
  companies: any[];
  copiedEmail: boolean;
  onCopiedEmailChange: (copied: boolean) => void;
  onContactChange: (contact: any) => void;
  onCompanyChange?: (leadId: string, companyId: string) => void;
  onNewQuote: (contact: any) => void;
  open?: boolean;
  onDismiss?: () => void;
}

export const ContactsPanel: React.FC<ContactsPanelProps> = ({
  activeContact,
  contacts,
  companyId,
  companies,
  copiedEmail,
  onCopiedEmailChange,
  onContactChange,
  onCompanyChange,
  onNewQuote,
  open = false,
  onDismiss,
}) => (
  <>
  {open && <button className="fixed inset-0 z-[210] bg-slate-950/70 backdrop-blur-sm 2xl:hidden" onClick={event => { event.stopPropagation(); onDismiss?.(); }} aria-label="Close contacts" />}
  <aside onClick={event => event.stopPropagation()} className={`fixed inset-y-0 left-0 z-[220] w-[min(92vw,24rem)] border-r border-white/10 bg-slate-950 p-5 sm:p-6 flex flex-col gap-6 shrink-0 overflow-y-auto custom-scrollbar transition-transform duration-200 2xl:static 2xl:w-72 2xl:translate-x-0 2xl:visible 2xl:bg-black/20 2xl:p-7 ${open ? 'visible translate-x-0' : 'invisible -translate-x-full pointer-events-none 2xl:pointer-events-auto'}`} aria-label="Personnel and company" data-testid="job-contacts-panel">
    <div className="flex items-center justify-between 2xl:hidden">
      <h3 className="type-heading text-lg text-white">Contacts ({contacts.length})</h3>
      <button onClick={event => { event.stopPropagation(); onDismiss?.(); }} className="min-w-11 min-h-11 rounded-xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-brand-neonBlue" aria-label="Close contacts"><X size={22} /></button>
    </div>
    <div className="flex flex-col gap-3">
      <h4 className="type-label flex items-center gap-2 text-slate-500">
        <LinkIcon size={12} className="text-brand-neonBlue" /> Corporate Entity Link
      </h4>
      <select
        className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-xs text-white outline-none focus:border-brand-neonBlue/50"
        value={companyId || ''}
        onChange={event => onCompanyChange?.(activeContact?.id, event.target.value)}
      >
        <option value="">-- Private Individual --</option>
        {companies?.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
      </select>
    </div>
    <div className="h-px bg-white/5" />
    <h4 className="type-label flex items-center gap-2 text-slate-500">
      <Users size={12} /> Personnel Node
    </h4>
    <div className="space-y-3 2xl:overflow-y-auto custom-scrollbar 2xl:pr-2">
      {contacts.map(contact => {
        const phone = contact.phone || contact.clientPhone;
        const email = contact.email || contact.clientEmail;
        const active = activeContact?.id === contact.id;
        return (
          <div
            key={contact.id}
            className={`w-full p-5 rounded-2xl border text-left transition-all ${active ? 'bg-brand-neonBlue/10 border-brand-neonBlue/30 text-white shadow-neon-blue-sm' : 'bg-white/5 border-white/5 text-slate-400 hover:border-white/20 hover:bg-white/10'}`}
          >
            <button type="button" aria-pressed={active} onClick={() => onContactChange(contact)} className="w-full min-h-11 text-left font-bold text-base mb-3 text-white tracking-tight focus-visible:ring-2 focus-visible:ring-brand-neonBlue">{[contact.first_name || contact.firstName, contact.last_name || contact.lastName].filter(Boolean).join(' ') || 'Contact'}<span className="block text-xs font-normal text-slate-400">View selected contact</span></button>
            <div className="space-y-2 text-xs">
              {email && (
                <div className="flex items-center gap-2 group">
                  <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`} target="quikpix_gmail_compose" rel="noopener noreferrer" className={`flex items-center gap-2 hover:text-brand-neonBlue transition-colors ${active ? 'text-blue-100' : 'text-slate-300'}`} title="Compose in Gmail" onClick={event => event.stopPropagation()}>
                    <Mail size={14} className={active ? 'text-brand-neonBlue' : 'text-slate-500'} /><span className="truncate">{email}</span>
                  </a>
                  <button
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      navigator.clipboard.writeText(email);
                      onCopiedEmailChange(true);
                      setTimeout(() => onCopiedEmailChange(false), 2000);
                    }}
                    className="min-w-11 min-h-11 flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 rounded-lg md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition-all shrink-0"
                    title="Copy Email Address"
                  >
                    {copiedEmail ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                  </button>
                </div>
              )}
              {phone && <p className={`flex items-center gap-2 ${active ? 'text-blue-100' : 'text-slate-300'}`}><Phone size={14} className={active ? 'text-brand-neonBlue' : 'text-slate-500'} /><span>{phone}</span></p>}
              {!email && !phone && <p className="text-xs italic text-slate-500">No contact details provided.</p>}
            </div>
          </div>
        );
      })}
    </div>
    <div className="mt-auto">
      <Button onClick={() => onNewQuote(activeContact)} className="w-full bg-brand-neonBlue text-slate-950 font-bold py-5 rounded-2xl"><Plus size={20} className="mr-2" /> New Quote</Button>
    </div>
  </aside>
  </>
);
